import { Modality } from '~/domain/dicom/modality';
import { ViewportId } from '../layout/types';
import {
    FrameOfReferenceUid,
    isKnownFor,
    SeriesInstanceUid,
} from '~/domain/dicom/uids';

/**
 * Pure core of the window/level fan-out policy: which series a named window
 * preset chosen on one tile reaches.
 *
 * Operates on a `GridSnapshot` (the per-tile frame of reference and
 * modality, derived from the layout + dataset adapter). The per-series
 * window/level lives in the engine/domain (`Image3D.windowLevel` on the
 * event bus); this core only answers which series a preset fans out to.
 *
 * Invariants: a preset fans out only within a frame of reference AND a
 * modality, never across either: a CT Hounsfield window never reaches an MR
 * or a PET of the same frame. A series of unknown modality, and a PET, whose
 * window is read in a unit of its own, reach their own series alone.
 */

/** One tile's context: its series, frame of reference and modality. */
export interface TileGeometryContext {
    readonly id: ViewportId;
    readonly forUid: FrameOfReferenceUid;
    readonly seriesUid: SeriesInstanceUid;
    /** The series' modality, normalised; `UNKNOWN` for an unrecognised value. */
    readonly modality: Modality;
}

/** The grid as this core sees it: just per-tile contexts. */
export interface GridSnapshot {
    readonly tiles: ReadonlyArray<TileGeometryContext>;
}

function tileContext(
    snapshot: GridSnapshot,
    id: ViewportId
): TileGeometryContext | undefined {
    return snapshot.tiles.find((t) => t.id === id);
}

/** Whether a series of this modality may take a window chosen on another. */
function linksAcrossSeries(modality: Modality): boolean {
    return modality !== Modality.UNKNOWN && modality !== Modality.PT;
}

/** Distinct series of one frame and one modality, in first-appearance order. */
function distinctSeriesLike(
    snapshot: GridSnapshot,
    ctx: TileGeometryContext
): SeriesInstanceUid[] {
    const series: SeriesInstanceUid[] = [];
    for (const tile of snapshot.tiles) {
        if (
            tile.forUid === ctx.forUid &&
            tile.modality === ctx.modality &&
            !series.includes(tile.seriesUid)
        ) {
            series.push(tile.seriesUid);
        }
    }
    return series;
}

/**
 * The series a named preset chosen on this tile fans out to: every distinct
 * series sharing the tile's known frame of reference and its modality, in
 * first-appearance order. A tile with an unknown frame, an unknown modality
 * or a PET targets only its own series.
 */
export function windowLevelTargets(
    snapshot: GridSnapshot,
    id: ViewportId
): ReadonlyArray<SeriesInstanceUid> {
    const ctx = tileContext(snapshot, id);
    if (!ctx) {
        return [];
    }
    if (!isKnownFor(ctx.forUid) || !linksAcrossSeries(ctx.modality)) {
        return [ctx.seriesUid];
    }
    return distinctSeriesLike(snapshot, ctx);
}
